import { useState, useEffect, useCallback, useMemo } from 'react';
import type { ToastVariant } from '@/components/ui/toast';

export interface ToastMessage {
  id: string;
  title?: string;
  description?: React.ReactNode;
  variant?: ToastVariant;
  duration?: number;
  action?: React.ReactElement;
}

interface ToastState {
  toasts: ToastMessage[];
}

const TOAST_LIMIT = 5;
const TOAST_DEFAULT_DURATION = 5000;

let toastCount = 0;

function genId() {
  toastCount = (toastCount + 1) % Number.MAX_VALUE;
  return toastCount.toString();
}

// Store toasts globally to share between components
let globalToasts: ToastMessage[] = [];
let listeners: Array<(state: ToastState) => void> = [];

function dispatch(action: {
  type: 'ADD_TOAST' | 'UPDATE_TOAST' | 'REMOVE_TOAST' | 'REMOVE_ALL';
  toast?: ToastMessage;
  id?: string;
}) {
  switch (action.type) {
    case 'ADD_TOAST':
      if (!action.toast) return;
      globalToasts = [action.toast, ...globalToasts].slice(0, TOAST_LIMIT);
      break;
    case 'UPDATE_TOAST':
      if (!action.toast) return;
      globalToasts = globalToasts.map((t) =>
        t.id === action.toast!.id ? { ...t, ...action.toast } : t
      );
      break;
    case 'REMOVE_TOAST':
      if (!action.id) return;
      globalToasts = globalToasts.filter((t) => t.id !== action.id);
      break;
    case 'REMOVE_ALL':
      globalToasts = [];
      break;
  }

  // Notify all listeners
  listeners.forEach((listener) => {
    listener({ toasts: globalToasts });
  });
}

// Interface for the toast function with shorthand methods
interface ToastFunction {
  (props: Omit<ToastMessage, 'id'>): string;
  success: (message: string | { title: string; description?: React.ReactNode }) => string;
  error: (message: string | { title: string; description?: React.ReactNode }) => string;
  warning: (message: string | { title: string; description?: React.ReactNode }) => string;
  info: (message: string | { title: string; description?: React.ReactNode }) => string;
}

/**
 * useToast Hook - Meridian Dark Design System
 *
 * Provides toast notification functionality with convenient methods.
 *
 * @example
 * const { toast, toasts, dismiss, dismissAll } = useToast();
 *
 * // Basic usage
 * toast({
 *   title: "Success",
 *   description: "Your changes have been saved.",
 *   variant: "success",
 * });
 *
 * // Shorthand methods
 * toast.success("Operation completed!");
 * toast.error("Something went wrong!");
 * toast.warning("Please review your changes.");
 * toast.info("New update available.");
 *
 * // With custom duration
 * toast({
 *   title: "Processing",
 *   description: "This might take a while...",
 *   duration: 10000, // 10 seconds
 * });
 *
 * // With action button
 * toast({
 *   title: "Undo",
 *   description: "Item deleted",
 *   action: <Button size="sm" onClick={handleUndo}>Undo</Button>,
 * });
 */
export function useToast() {
  const [state, setState] = useState<ToastState>({ toasts: globalToasts });

  useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, []);

  const baseToast = useCallback((props: Omit<ToastMessage, 'id'>) => {
    const id = genId();
    const duration = props.duration ?? TOAST_DEFAULT_DURATION;

    const toastMessage: ToastMessage = {
      ...props,
      id,
      duration,
    };

    dispatch({ type: 'ADD_TOAST', toast: toastMessage });

    // Auto-dismiss after duration
    if (duration !== Infinity) {
      setTimeout(() => {
        dispatch({ type: 'REMOVE_TOAST', id });
      }, duration);
    }

    return id;
  }, []);

  // Create the toast function with shorthand methods
  const toast: ToastFunction = useMemo(
    () =>
      Object.assign(baseToast, {
        success: (message: string | { title: string; description?: React.ReactNode }) =>
          baseToast(
            typeof message === 'string'
              ? { title: message, variant: 'success' }
              : { ...message, variant: 'success' }
          ),
        error: (message: string | { title: string; description?: React.ReactNode }) =>
          baseToast(
            typeof message === 'string'
              ? { title: message, variant: 'error' }
              : { ...message, variant: 'error' }
          ),
        warning: (message: string | { title: string; description?: React.ReactNode }) =>
          baseToast(
            typeof message === 'string'
              ? { title: message, variant: 'warning' }
              : { ...message, variant: 'warning' }
          ),
        info: (message: string | { title: string; description?: React.ReactNode }) =>
          baseToast(
            typeof message === 'string'
              ? { title: message, variant: 'info' }
              : { ...message, variant: 'info' }
          ),
      }),
    [baseToast]
  );

  const dismiss = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_TOAST', id });
  }, []);

  const dismissAll = useCallback(() => {
    dispatch({ type: 'REMOVE_ALL' });
  }, []);

  return {
    toast,
    toasts: state.toasts,
    dismiss,
    dismissAll,
  };
}
